import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Handshake, FileSignature, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";

interface Deal {
  id: string;
  title: string;
  amount: number | null;
  ai_win_probability: number | null;
  status: string | null;
}

interface ClosingStepProps {
  deals: Deal[] | undefined;
  selectedDealIds: string[];
  onToggleDeal: (dealId: string) => void;
  isCreatingContracts: boolean;
  onCreateContracts: () => void;
  createdContractCount: number;
  error?: string;
}

export function ClosingStep({
  deals,
  selectedDealIds,
  onToggleDeal,
  isCreatingContracts,
  onCreateContracts,
  createdContractCount,
  error
}: ClosingStepProps) {
  // Include deals with 'open' status or null status (newly created)
  const openDeals = deals?.filter(d => d.status === 'open' || d.status === null) || [];
  // Show deals with 60%+ win probability (lowered threshold to include more wizard-created deals)
  const readyToClose = openDeals.filter(d => (d.ai_win_probability || 0) >= 60);

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground">
        AI detects closing signals and generates contracts for high-probability deals.
      </p>
      
      {error && (
        <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">{error}</span>
        </div>
      )}
      
      <Card className="border-primary/50 bg-primary/5">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Handshake className="h-8 w-8 text-primary" />
            <div>
              <div className="font-medium">AI Closing Assistant</div>
              <div className="text-sm text-muted-foreground">
                {readyToClose.length} deals ready to close (60%+ win probability)
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <div className="grid grid-cols-2 gap-3">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-primary">{openDeals.length}</div>
            <div className="text-sm text-muted-foreground">Open Deals</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-500">{readyToClose.length}</div>
            <div className="text-sm text-muted-foreground">Ready to Close</div>
          </CardContent>
        </Card>
      </div>
      
      <div className="space-y-2 max-h-[150px] overflow-y-auto">
        {readyToClose.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground">
            No deals ready to close yet. Continue nurturing your pipeline.
          </div>
        ) : (
          readyToClose.map(deal => (
            <div 
              key={deal.id}
              className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg hover:bg-muted/70 cursor-pointer"
              onClick={() => onToggleDeal(deal.id)}
            >
              <CheckCircle2 
                className={`h-5 w-5 ${selectedDealIds.includes(deal.id) ? 'text-primary' : 'text-muted-foreground'}`}
              />
              <div className="flex-1">
                <div className="font-medium">{deal.title}</div>
                <div className="text-sm text-muted-foreground">
                  ${deal.amount?.toLocaleString() || 0}
                </div>
              </div>
              <Badge variant="default" className="bg-green-500">
                {deal.ai_win_probability}%
              </Badge>
            </div>
          ))
        )}
      </div>
      
      <Button 
        onClick={onCreateContracts}
        disabled={isCreatingContracts || selectedDealIds.length === 0}
        className="w-full"
      >
        {isCreatingContracts ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Creating Contracts...
          </>
        ) : (
          <>
            <FileSignature className="h-4 w-4 mr-2" />
            Create Contracts for {selectedDealIds.length} Deals
          </>
        )}
      </Button>
      
      {createdContractCount > 0 && (
        <div className="flex items-center gap-2 p-3 bg-green-500/10 text-green-600 rounded-lg animate-fade-in">
          <FileSignature className="h-4 w-4" />
          <span className="text-sm">{createdContractCount} contracts created and ready for signature</span>
        </div>
      )}
    </div>
  );
}
